import * as THREE from 'three';
import { PHOTO_SURFACES, S, SURFACE_METRES, SURFACE_ROUGHNESS } from '../gen/mesh/surfaces';

const L = PHOTO_SURFACES.length;
const LAYER = (name: (typeof PHOTO_SURFACES)[number]) => PHOTO_SURFACES.indexOf(name);

/** Mean albedo per layer, for re-tinting (from public/textures/manifest.json; grey until loaded). */
type Manifest = Record<string, { id: string; mean: [number, number, number] }>;

/**
 * Photo materials (CC0, public/textures, fetched by scripts/fetch_textures.py) as two texture arrays,
 * one layer per photo surface: albedo (sRGB) and normal.xy + roughness. `patch` teaches a material to
 * texture every vertex whose `surface` attribute is set (DECISIONS "Surfaces"):
 * - UVs are projected from position (walls: horizontal tangent × height; floors and ceilings: x/z), so
 *   the pattern runs on across the separate quads of a wall and across tiles. Positions are
 *   tile-local plus the tile offset modulo 1024 m, so they stay precise anywhere in the world.
 * - albedo = texture × vertex colour ÷ the texture's mean colour: the generator's colour stays the
 *   average colour, and the texture adds the detail.
 * - large ground surfaces blend in a second, rotated sample at another scale and a macro noise, to
 *   break up tiling; the terrain blends grass, dirt and rock by slope.
 * Until the textures are loaded, surfaces render as plain colour, as before.
 */
export class SurfaceTextures {
  readonly albedo: THREE.DataArrayTexture;
  readonly nr: THREE.DataArrayTexture;
  private readonly uniforms = {
    uSurfAlb: { value: null as THREE.DataArrayTexture | null },
    uSurfNR: { value: null as THREE.DataArrayTexture | null },
    uSurfMean: { value: Array.from({ length: L }, () => new THREE.Vector3(0.5, 0.5, 0.5)) },
    uSurfScale: { value: PHOTO_SURFACES.map((n) => 1 / SURFACE_METRES[n]) },
    uSurfRough: { value: PHOTO_SURFACES.map((n) => new THREE.Vector2(...SURFACE_ROUGHNESS[n])) },
    uSurfReady: { value: 0 },
    uSurfNormal: { value: 1 },
  };

  constructor(readonly size = 512) {
    const make = (srgb: boolean) => {
      const t = new THREE.DataArrayTexture(new Uint8Array(size * size * 4 * L), size, size, L);
      t.format = THREE.RGBAFormat;
      t.type = THREE.UnsignedByteType;
      t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.minFilter = THREE.LinearMipmapLinearFilter;
      t.magFilter = THREE.LinearFilter;
      t.generateMipmaps = true;
      t.anisotropy = 8;
      return t;
    };
    this.albedo = make(true);
    this.nr = make(false);
    this.uniforms.uSurfAlb.value = this.albedo;
    this.uniforms.uSurfNR.value = this.nr;
  }

  /** Load every layer (in parallel); surfaces switch on when all are in. */
  async load(base = import.meta.env.BASE_URL + 'textures/'): Promise<void> {
    const manifest = (await (await fetch(base + 'manifest.json')).json()) as Manifest;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = this.size;
    const g = canvas.getContext('2d', { willReadFrequently: true })!;
    const pixels = async (url: string) => {
      const bmp = await createImageBitmap(await (await fetch(url)).blob());
      return bmp;
    };
    const jobs = PHOTO_SURFACES.map(async (name, layer) => {
      const [a, n] = await Promise.all([pixels(`${base}${name}_albedo.jpg`), pixels(`${base}${name}_nr.jpg`)]);
      return { layer, a, n, mean: manifest[name]?.mean ?? [0.5, 0.5, 0.5] };
    });
    const layerBytes = this.size * this.size * 4;
    for (const { layer, a, n, mean } of await Promise.all(jobs)) {
      for (const [bmp, tex] of [
        [a, this.albedo],
        [n, this.nr],
      ] as const) {
        g.clearRect(0, 0, this.size, this.size);
        g.drawImage(bmp, 0, 0, this.size, this.size);
        (tex.image.data as Uint8Array).set(g.getImageData(0, 0, this.size, this.size).data, layer * layerBytes);
        bmp.close();
      }
      this.uniforms.uSurfMean.value[layer].set(Math.max(0.03, mean[0]), Math.max(0.03, mean[1]), Math.max(0.03, mean[2]));
    }
    this.albedo.needsUpdate = true;
    this.nr.needsUpdate = true;
    this.uniforms.uSurfReady.value = 1;
  }

  /** Whether the photo layers are in (the loading screen waits for them). */
  get loaded(): boolean {
    return this.uniforms.uSurfReady.value === 1;
  }

  /** Normal maps on or off (the "low" preset turns them off). */
  set normals(on: boolean) {
    this.uniforms.uSurfNormal.value = on ? 1 : 0;
  }

  /**
   * Patch `m` (MeshStandardMaterial, or MeshBasicMaterial with `basic`) to texture surfaced vertices.
   * `terrain` textures every vertex as terrain (the terrain mesh has no surface attribute). Chains any
   * onBeforeCompile the material already has; call before Shadows.setup.
   */
  patch(m: THREE.Material, opts: { terrain?: boolean; basic?: boolean } = {}): void {
    const prev = m.onBeforeCompile;
    const u = this.uniforms;
    m.onBeforeCompile = (sh, r) => {
      prev.call(m, sh, r);
      Object.assign(sh.uniforms, u);
      sh.vertexShader = sh.vertexShader.replace('#include <common>', `#include <common>\n${VERT_DECL}`).replace(
        '#include <project_vertex>',
        `#include <project_vertex>
        vSurface = ${opts.terrain ? `${S.terrain}.0` : 'surface'};
        vSurfN = normalize(mat3(modelMatrix) * normal);
        vSurfPos = mat3(modelMatrix) * transformed + mod(modelMatrix[3].xyz, 1024.0);`,
      );
      let fs = sh.fragmentShader.replace('#include <common>', `#include <common>\n${FRAG_DECL}`).replace(
        '#include <color_fragment>',
        `#include <color_fragment>
        SurfSample surf;
        surf.ok = uSurfReady > 0.5 && vSurface > 0.5;
        if (surf.ok) {
          surf = surfSample(vSurface, vSurfPos, normalize(vSurfN));
          if (surf.absolute) {
            // Terrain: the photos' own colours; the vertex colour only carries the snow line.
            float snow = smoothstep(0.72, 0.9, min(diffuseColor.r, min(diffuseColor.g, diffuseColor.b)));
            diffuseColor.rgb = mix(surf.albedo, vec3(0.93, 0.94, 0.97), snow);
          } else diffuseColor.rgb *= surf.albedo;
        }`,
      );
      if (!opts.basic) {
        fs = fs
          .replace(
            '#include <roughnessmap_fragment>',
            `#include <roughnessmap_fragment>
            if (surf.ok) roughnessFactor = surf.rough;`,
          )
          .replace(
            '#include <normal_fragment_maps>',
            `#include <normal_fragment_maps>
            if (surf.ok && uSurfNormal > 0.5) normal = normalize((viewMatrix * vec4(surf.normal, 0.0)).xyz);`,
          );
      }
      sh.fragmentShader = fs;
    };
    const prevKey = m.customProgramCacheKey.bind(m);
    m.customProgramCacheKey = () => `${prevKey()}|surf${opts.terrain ? 'T' : ''}${opts.basic ? 'B' : ''}`;
    m.needsUpdate = true;
  }
}

const VERT_DECL = /* glsl */ `
attribute float surface;
varying float vSurface;
varying vec3 vSurfPos;
varying vec3 vSurfN;`;

const FRAG_DECL = /* glsl */ `
uniform sampler2DArray uSurfAlb;
uniform sampler2DArray uSurfNR;
uniform vec3 uSurfMean[${L}];
uniform float uSurfScale[${L}];
uniform vec2 uSurfRough[${L}];
uniform float uSurfReady;
uniform float uSurfNormal;
varying float vSurface;
varying vec3 vSurfPos;
varying vec3 vSurfN;

struct SurfSample { bool ok; bool absolute; vec3 albedo; float rough; vec3 normal; };

float surfHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float surfNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(surfHash(i), surfHash(i + vec2(1.0, 0.0)), f.x), mix(surfHash(i + vec2(0.0, 1.0)), surfHash(i + vec2(1.0, 1.0)), f.x), f.y);
}

// One layer: albedo normalised by its mean, roughness, tangent-space normal (xy, z).
void surfLayer(int layer, vec2 uv, bool breakUp, out vec3 alb, out float rough, out vec2 nxy) {
  vec2 st = uv * uSurfScale[layer];
  vec4 a = texture(uSurfAlb, vec3(st, float(layer)));
  vec4 n = texture(uSurfNR, vec3(st, float(layer)));
  if (breakUp) {
    // A second sample, rotated and at another scale, faded in by low-frequency noise.
    vec2 st2 = mat2(0.6, -0.8, 0.8, 0.6) * st * 0.43 + 0.37;
    float w = smoothstep(0.35, 0.65, surfNoise(uv * 0.045));
    a = mix(a, texture(uSurfAlb, vec3(st2, float(layer))), w);
    n = mix(n, texture(uSurfNR, vec3(st2, float(layer))), w);
  }
  alb = a.rgb / uSurfMean[layer];
  rough = mix(uSurfRough[layer].x, uSurfRough[layer].y, n.b);
  nxy = n.rg * 2.0 - 1.0;
}

SurfSample surfSample(float id, vec3 p, vec3 N) {
  SurfSample s;
  s.ok = true;
  s.absolute = false;
  // Projection: floors/ceilings from above, everything steeper along its horizontal tangent.
  vec3 T;
  vec3 B;
  vec2 uv;
  if (abs(N.y) > 0.6) {
    T = vec3(1.0, 0.0, 0.0);
    B = vec3(0.0, 0.0, N.y > 0.0 ? -1.0 : 1.0);
    uv = vec2(p.x, N.y > 0.0 ? -p.z : p.z);
  } else {
    T = normalize(vec3(N.z, 0.0, -N.x));
    B = vec3(0.0, 1.0, 0.0);
    uv = vec2(dot(p, T), p.y);
  }
  vec3 alb;
  float rough;
  vec2 nxy;
  int sid = int(id + 0.5);
  if (sid == ${S.terrain}) {
    // Grass, dirt patches, rock on steep ground.
    vec3 a1; float r1; vec2 n1;
    vec3 a2; float r2; vec2 n2;
    vec3 a3; float r3; vec2 n3;
    surfLayer(${LAYER('grass')}, uv, true, a1, r1, n1);
    surfLayer(${LAYER('dirt')}, uv, true, a2, r2, n2);
    surfLayer(${LAYER('rock')}, uv, false, a3, r3, n3);
    float dirt = smoothstep(0.55, 0.8, surfNoise(p.xz * 0.02) * 0.7 + surfNoise(p.xz * 0.11) * 0.3);
    float rock = smoothstep(0.62, 0.8, 1.0 - N.y);
    // Absolute colours: undo the mean normalisation.
    a1 *= uSurfMean[${LAYER('grass')}] * 1.1;
    a2 *= uSurfMean[${LAYER('dirt')}];
    a3 *= uSurfMean[${LAYER('rock')}];
    alb = mix(mix(a1, a2, dirt), a3, rock);
    s.absolute = true;
    rough = mix(mix(r1, r2, dirt), r3, rock);
    nxy = mix(mix(n1, n2, dirt), n3, rock);
  } else {
    int layer = sid - 1;
    // Irregular materials get the tiling break-up; regular ones (bricks, pavers, tiles) don't.
    bool breakUp = layer == ${LAYER('asphalt')} || layer == ${LAYER('asphaltWorn')} || layer == ${LAYER('grass')} || layer == ${LAYER('dirt')} || layer == ${LAYER('concrete')} || layer == ${LAYER('gravel')} || layer == ${LAYER('tar')};
    surfLayer(layer, uv, breakUp, alb, rough, nxy);
    if (layer == ${LAYER('asphalt')}) {
      // Worn patches on the road.
      vec3 a2; float r2; vec2 n2;
      surfLayer(${LAYER('asphaltWorn')}, uv, false, a2, r2, n2);
      float w = smoothstep(0.6, 0.85, surfNoise(p.xz * 0.05) * 0.75 + surfNoise(p.xz * 0.4) * 0.25);
      alb = mix(alb, a2 * 1.08, w * 0.8);
      rough = mix(rough, r2, w);
    }
  }
  // Macro variation: +-10% brightness over tens of metres, so repeats don't line up visibly.
  alb *= 0.9 + 0.2 * surfNoise(p.xz * 0.03 + p.y * 0.05);
  s.albedo = alb;
  s.rough = clamp(rough, 0.04, 1.0);
  s.normal = normalize(T * nxy.x + B * nxy.y + N * max(0.2, sqrt(max(0.0, 1.0 - dot(nxy, nxy)))));
  return s;
}`;
